<template>
  <div class="birth-picker-container">
    <v-row no-gutters align-start justify-start class="birth-picker-layout">
      <v-col cols="10">
      <v-text-field
        class="input-field focused-border"
        id="DOB"
        outline
        height="48"
        :label="field.m === 1 ? messages.dateOfBirthLabel + ' *': messages.dateOfBirthLabel"
        :aria-label="dateOfBirthLabelWithFormat"
        v-model="date"
        :rules="field.m === 1 ? [requiredRule, birthDateFormatRule] : [birthDateFormatRule]"
        :hint="messages.dateFormatHint ? messages.dateFormatHint : dateOfBirthFormat"
        persistent-hint
        clearable
        ref="DOB"
        @blur="refreshDateOfBirth()"
      ></v-text-field>
      </v-col>
      <v-col cols="2">
      <v-menu
        ref="dobMenu"
        v-model="dobMenu"
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            tabindex="-1"
            icon
            id="dobPickerButton"
          >
            <v-icon>event</v-icon>
          </v-btn>
        </template>
        <v-date-picker
          ref="dobPicker"
          v-model="datePick"
          :max="new Date().toISOString().substr(0, 10)"
          :min="getMinDatePiker"
          @change="saveDateOfBirthFromPicker"
          :first-day-of-week="firstDayOfWeek"
          :locale="locale"
          no-title
        ></v-date-picker>
      </v-menu>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  props: [
    'field',
    'dob'
  ],
  data: () => ({
    requiredRule: false,
    birthDateFormatRule: false,
    inputFormat: 'YYYYMMDD',
    date: null,
    datePick: null,
    dobMenu: false,
    dateMemory: null
  }),
  watch: {
    dobMenu (val) {
      // if (this.funcDebug) console.log('BirthPicker:watch:dobMenu')
      // set the datepicker to use year selection first
      val && setTimeout(() => (this.$refs.dobPicker.activePicker = 'YEAR'))
    },
    datePick (val) {
      if (this.funcDebug) console.log('BirthPicker:watch:datePick')
      // if val is null, then don't do anything with it, value was set to null to reset the picket
      if (val) {
        // convert date
        var momentDate = moment(val, 'YYYY-MM-DD', true)
        this.date = momentDate.format(this.dateOfBirthFormat)
        // we leave the picker so focus on DOB
        this.$nextTick(() => {
          this.$refs.DOB.focus()
        })
      }
    },
    date () {
      // reset picker if there was a date in it (need to do this so if date is changed, picker can pick again the same date)
      this.datePick = null
      this.save()
    },
    dateOfBirthFormat () {
      this.refreshDateOfBirth()
    }
  },
  computed: {
    ...mapState([
      'messages',
      'funcDebug',
      'selectedLanguage'
    ]),
    getMaxAge () {
      return this.dateOfBirthFormat.indexOf('YYYY') > -1 ? 120 : 100
    },
    dateOfBirthFormat () {
      return this.selectedLanguage.inputDateFormat === '' ? 'YYYYMMDD' : this.selectedLanguage.inputDateFormat
    },
    dateOfBirthLabelWithFormat () {
      var dobl = this.selectedLanguage.inputDateFormat === '' ? 'YYYYMMDD' : this.selectedLanguage.inputDateFormat
      return this.messages.ariaLabelDateOfBirth + ' ' + dobl
    },
    locale () {
      return this.selectedLanguage.lang + '-' + this.selectedLanguage.countryCodeLang
    },
    firstDayOfWeek () {
      return this.selectedLanguage.firstDayOfWeek ? this.selectedLanguage.firstDayOfWeek : '1'
    },
    getMinDatePiker () {
      let dateMin = moment().startOf('year').add(-(this.getMaxAge - 1), 'years')
      return dateMin.format('YYYY-MM-DD')
    }
  },
  methods: {
    saveDateOfBirthFromPicker (date) {
      if (this.funcDebug) console.log('BirthPicker:method:saveDateOfBirthFromPicker')
      // save to close the picker menu
      this.$refs.dobMenu.save(date)
    },
    save () {
      if (this.funcDebug) console.log('BirthPicker:method:save')
      var momentDate = moment(this.date, this.dateOfBirthFormat, true)
      var toSaveDate = momentDate.format('YYYY-MM-DD')
      this.$emit('birth-selected', toSaveDate)
      this.$emit('temp-birth', this.date)
    },
    clearDateOfBirth () {
      if (this.funcDebug) console.log('BirthPicker:method:clearDateOfBirth')
      this.date = null
    },
    refreshDateOfBirth () {
      var _this = this
      if (this.funcDebug) console.log('BirthPicker:method:refreshDateOfBirth')
      this.dateMemory = this.date
      this.date = null
      setTimeout(() => {
        _this.date = _this.dateMemory
        _this.$emit('temp-birth', _this.date)
      }, 100)
    }
  },
  mounted () {
    if (this.funcDebug) console.log('BirthPicker:mounted')
    var _this = this

    // set previously added birth date
    this.date = this.dob ? this.dob : null

    // rewrite the 2 digits year conversion of moment
    moment.parseTwoDigitYear = (yearString) => {
      var dateNow = new Date()
      var thisYear = parseInt(dateNow.getFullYear().toString().substring(2))
      return parseInt(yearString) + (parseInt(yearString) > thisYear ? 1900 : 2000)
    }
    // Initiate validation rules
    var dobFormatTxt = _this.messages.dateFormatHint ? _this.messages.dateFormatHint : _this.dateOfBirthFormat
    this.requiredRule = v => !!v || (_this.messages.fieldRequired === undefined ? false : _this.messages.fieldRequired) + ' (' + dobFormatTxt + ')'
    this.birthDateFormatRule =
      v => {
        // parse it through moment to check if it is asked format
        var momentDate = moment(_this.date, _this.dateOfBirthFormat, true)
        var isCorrect = momentDate.isValid()
        var showRangeErrorMessage = false
        // check date range
        var dateNow = moment()
        if (isCorrect) {
          var thisYear = dateNow.year()
          var year = momentDate.year()
          if ((year > thisYear) || (year <= (thisYear - _this.getMaxAge)) || (dateNow < momentDate)) {
            isCorrect = false
            showRangeErrorMessage = true
          }
        }
        // also accept empty if not mandatory
        if ((_this.field.m !== 1) && ((v === null) || (v === ''))) {
          isCorrect = true
          showRangeErrorMessage = false
        }
        return isCorrect || (_this.messages.dateOfBirthInvalid === undefined ? false : _this.messages.dateOfBirthInvalid + ' ' + (_this.messages.dateFormatHint ? _this.messages.dateFormatHint : _this.dateOfBirthFormat) + (showRangeErrorMessage ? ' ' + _this.messages.dateOfBirthInvalidRange : ''))
      }
  }
}
</script>
<style lang="scss" scoped>

.birth-picker-layout {
  width: 550px;
  .v-input.input-field.v-text-field {
    width: 420px;
  }
}

@media only screen and (max-width: 599px) {
  .birth-picker-layout {
    width: 100% !important;
    margin: 0 !important;
  }
}

</style>
